// G1PriorDecisionsImport — cross-run decision carry-forward, manual-import
// variant (Architect Decisions Downstream Flow Plan §8 follow-up).
//
// Surfaces a banner at the top of the G1 panel when a prior run of
// the same project has captured architect decisions. The architect
// can:
//
//   * Click "Import all" to copy every prior answer into the current
//     run's G1 draft (one click for the common "same intent" path).
//   * Click "Review prior decisions" to inspect the prior run's
//     answers in a side panel before deciding whether to import.
//   * Decide not to import — the banner is dismissable and the
//     current run's draft stays untouched.
//
// Design rationale: the architect's prior answer is never silently
// auto-leaked into the new run's audit record. Every imported
// decision carries an "imported from run-N" provenance line so a
// compliance reviewer can trace what was inherited vs. what was
// freshly affirmed.

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  CheckSquare,
  ChevronDown,
  ChevronUp,
  Inbox,
  Loader2,
} from 'lucide-react'

import { api } from '@/lib/api'
import type { PriorDecisionRecord } from '@/lib/api'

export interface G1PriorDecisionsImportProps {
  projectId: string
  /** The current run's id. The banner only renders when there's a
   *  prior run *strictly less than* this one with captured decisions. */
  runId: number
  /** Called with the prior-run records when the architect clicks
   *  "Import all". The G1 gate panel translates these into per-DU
   *  draft updates so the architect sees them pre-filled. */
  onImportAll: (decisions: PriorDecisionRecord[]) => void
}

export function G1PriorDecisionsImport({
  projectId,
  runId,
  onImportAll,
}: G1PriorDecisionsImportProps) {
  const [expanded, setExpanded] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const [imported, setImported] = useState(false)

  const q = useQuery({
    queryKey: ['prior-decisions', projectId, runId],
    queryFn: () => api.getPriorDecisions(projectId, runId),
    retry: false,
  })

  if (dismissed) return null
  if (q.isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
        <Loader2 className="h-3 w-3 animate-spin" /> Checking for prior-run
        decisions…
      </div>
    )
  }
  if (q.error || !q.data || q.data.available === false) {
    return null
  }

  const { prior_run_id, prior_run_generated_at, decisions } = q.data
  const decidedAt = prior_run_generated_at
    ? new Date(prior_run_generated_at).toLocaleDateString()
    : 'an earlier date'

  return (
    <div
      data-testid="g1-prior-decisions-banner"
      className="rounded-lg border border-indigo-700/50 bg-indigo-950/30 p-3 mb-4"
    >
      <div className="flex items-start gap-2">
        <Inbox className="h-4 w-4 mt-0.5 shrink-0 text-indigo-300" />
        <div className="min-w-0 flex-1">
          <p className="text-sm text-slate-100">
            <strong>Run-{String(prior_run_id).padStart(3, '0')}</strong>{' '}
            closed {decisions.length} decision
            {decisions.length === 1 ? '' : 's'} on {decidedAt}.
          </p>
          <p className="text-[11.5px] text-slate-400 mt-0.5">
            Pull those answers into the current run's G1 draft, or
            review them first before deciding. Each imported decision
            keeps an "imported from run-
            {String(prior_run_id).padStart(3, '0')}" provenance line
            on the audit trail.
          </p>
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          {!imported && (
            <button
              type="button"
              data-testid="g1-prior-decisions-import-all"
              onClick={() => {
                onImportAll(decisions)
                setImported(true)
              }}
              className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-indigo-500"
            >
              <CheckSquare className="h-3 w-3" /> Import all
            </button>
          )}
          {imported && (
            <span
              data-testid="g1-prior-decisions-imported-badge"
              className="inline-flex items-center gap-1.5 rounded-md border border-emerald-600/50 bg-emerald-950/30 px-2.5 py-1 text-xs font-semibold text-emerald-200"
            >
              <CheckSquare className="h-3 w-3" /> Imported
            </span>
          )}
          <button
            type="button"
            data-testid="g1-prior-decisions-review"
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 px-2.5 py-1 text-xs text-slate-300 hover:bg-white/5"
          >
            {expanded ? (
              <>
                <ChevronUp className="h-3 w-3" /> Hide
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3" /> Review
              </>
            )}
          </button>
          <button
            type="button"
            data-testid="g1-prior-decisions-dismiss"
            onClick={() => setDismissed(true)}
            className="text-[10.5px] text-slate-500 hover:text-slate-300"
          >
            Dismiss
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-indigo-700/40 space-y-2.5">
          {decisions.map((d) => (
            <div
              key={d.id}
              className="rounded-md border border-slate-700/50 bg-slate-900/40 p-2.5"
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono text-[10.5px] text-indigo-300">
                  {d.id}
                </span>
                <span className="alm-chip text-[9.5px]">
                  {d.status}
                </span>
                {d.source_gate && (
                  <span className="alm-chip text-[9.5px]">
                    {d.source_gate}
                  </span>
                )}
              </div>
              <p className="text-[12.5px] text-slate-200">{d.question}</p>
              <p className="text-[10.5px] text-slate-500 mt-0.5">
                Owner: {d.stakeholder}
              </p>
              {d.decision && (
                <p className="text-[12px] text-emerald-200 mt-1.5">
                  → {d.decision}
                </p>
              )}
              {d.rationale && (
                <p className="text-[11px] text-amber-200/80 mt-0.5 italic">
                  Rationale: {d.rationale}
                </p>
              )}
              {d.alternatives_considered.length > 0 && (
                <div className="mt-1.5 text-[10.5px] text-slate-500">
                  <span className="uppercase tracking-wider mr-1">
                    Alternatives:
                  </span>
                  {d.alternatives_considered.join(' · ')}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
